import type { ModuleKey } from '@nova/shared';
import {
  Activity,
  Bot,
  Bell,
  CalendarRange,
  LayoutDashboard,
  ListChecks,
  Lock,
  Package,
  Radar,
  Settings,
  Share2,
  Laptop,
  Target,
  TrendingUp,
} from 'lucide-react';

/** Sidebar groups. Items without a section sit at the top, above the groups. */
export type NavSection = 'MARKED' | 'SALG' | 'UTSTYR' | 'ADMIN';

export const NAV_SECTIONS: { key: NavSection; label: string }[] = [
  { key: 'MARKED', label: 'Marked' },
  { key: 'SALG', label: 'Salg' },
  { key: 'UTSTYR', label: 'Utstyr' },
  { key: 'ADMIN', label: 'Admin' },
];

export interface NavItem {
  to: string;
  label: string;
  /** Shorter label for the mobile bottom bar. */
  mobileLabel?: string;
  Icon: typeof LayoutDashboard;
  adminOnly?: boolean;
  /** Only superadmins see it — the private leadership report. */
  superadminOnly?: boolean;
  /** Hidden unless the user has this module (no access ⇒ no menu entry). */
  module?: ModuleKey;
  /** Which sidebar group the item belongs to. Omitted = top-level. */
  section?: NavSection;
  /** Shown in the mobile bottom navigation rather than under "Mer". */
  mobilePrimary?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  // Top-level: the home overview and cross-module alerts.
  {
    to: '/',
    label: 'Dashbord',
    mobileLabel: 'Status',
    Icon: LayoutDashboard,
    module: 'MARKETING',
    mobilePrimary: true,
  },
  {
    to: '/varsler',
    label: 'Varsler',
    mobileLabel: 'Varsler',
    Icon: Bell,
    mobilePrimary: true,
  },

  // ── Marked (markedsføringsmodulen) ──────────────────────────────────────────
  {
    to: '/plan',
    label: 'Redaksjonsplan',
    mobileLabel: 'Plan',
    Icon: CalendarRange,
    module: 'MARKETING',
    section: 'MARKED',
    mobilePrimary: true,
  },
  { to: '/omrader', label: 'Områder', Icon: Package, module: 'MARKETING', section: 'MARKED' },
  { to: '/agenter', label: 'Agenter', Icon: Bot, module: 'MARKETING', section: 'MARKED' },
  { to: '/kpi', label: 'KPI', Icon: TrendingUp, module: 'MARKETING', section: 'MARKED' },
  { to: '/some', label: 'SoMe', Icon: Share2, module: 'MARKETING', section: 'MARKED' },
  { to: '/aktivitet', label: 'Aktivitet', Icon: Activity, module: 'MARKETING', section: 'MARKED' },

  // ── Salg ────────────────────────────────────────────────────────────────────
  {
    to: '/salg',
    label: 'Anbud',
    mobileLabel: 'Anbud',
    Icon: Radar,
    module: 'SALES',
    section: 'SALG',
  },
  {
    to: '/marked',
    label: 'Marked og kunder',
    mobileLabel: 'Marked',
    Icon: Target,
    module: 'SALES',
    section: 'SALG',
  },
  {
    to: '/prioritering',
    label: 'Prioritering',
    mobileLabel: 'Prioritet',
    Icon: ListChecks,
    module: 'SALES',
    section: 'SALG',
  },

  // ── Utstyr og lisenser ──────────────────────────────────────────────────────
  {
    to: '/utstyr',
    label: 'Utstyr og lisenser',
    mobileLabel: 'Utstyr',
    Icon: Laptop,
    module: 'ASSETS',
    section: 'UTSTYR',
  },

  // ── Admin ───────────────────────────────────────────────────────────────────
  { to: '/ledelse', label: 'Ledelse', Icon: Lock, superadminOnly: true, section: 'ADMIN' },
  { to: '/admin', label: 'Admin', Icon: Settings, adminOnly: true, section: 'ADMIN' },
];

/**
 * Split an already-access-filtered list of nav items into the top-level items
 * and the non-empty sections, in `NAV_SECTIONS` order. Shared by the desktop
 * rail and the mobile "Mer" sheet so grouping stays consistent.
 */
export function groupNavItems(items: NavItem[]): {
  top: NavItem[];
  sections: { key: NavSection; label: string; items: NavItem[] }[];
} {
  const top = items.filter((i) => !i.section);
  const sections = NAV_SECTIONS.map((s) => ({
    ...s,
    items: items.filter((i) => i.section === s.key),
  })).filter((s) => s.items.length > 0);
  return { top, sections };
}
